import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { MapPin, Calendar, BookOpen } from "lucide-react";
import { SectionTitle } from "./shared";

// Animation for details
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
    gap: 3rem;
  }
`;

const EducationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EducationCard = styled.div`
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${(props) => (props.$isActive ? "rgba(255, 255, 255, 0.05)" : "transparent")};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const EducationCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EducationCardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0;
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  ${EducationCard}:hover & {
    opacity: 0.9;
  }
`;

const DetailsPanel = styled.div`
  position: relative;
  /* height: 600px; */
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 3rem 2rem 5rem 2rem;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  overflow: hidden;
`;

const DetailsPanelContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const EducationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  white-space: pre-line;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const Educations = ({ educations }) => {
  const [activeEducation, setActiveEducation] = useState(educations?.[0]);

  if (!educations || educations.length === 0) return null;

  return (
    <Container>
      <SectionTitle>Educations</SectionTitle>
      <Grid>
        {/* Left Panel: List of Education */}
        <EducationList>
          {educations.map((edu) => (
            <EducationCard
              key={edu.id}
              $isActive={activeEducation?.id === edu.id}
              onMouseEnter={() => setActiveEducation(edu)}
            >
              <EducationCardContent>
                <EducationCardTitle>{edu.name}</EducationCardTitle>
              </EducationCardContent>
            </EducationCard>
          ))}
        </EducationList>

        {/* Right Panel: Education Details */}
        <DetailsPanel>
          <DetailsPanelContent>
            {activeEducation ? (
              <EducationDetails>
                <Title>{activeEducation.name}</Title>
                <MetaInfo>
                  <BookOpen size={16} /> {activeEducation.degree} in {activeEducation.field_of_study}
                </MetaInfo>
                <MetaInfo>
                  <MapPin size={16} /> {activeEducation.university}
                </MetaInfo>
                <MetaInfo>
                  <Calendar size={16} />
                  {activeEducation.start_date} - {activeEducation.end_date}
                </MetaInfo>
                {activeEducation.description && (
                  <Description>{activeEducation.description}</Description>
                )}
              </EducationDetails>
            ) : (
              <Title>Education</Title>
            )}
          </DetailsPanelContent>
        </DetailsPanel>
      </Grid>
    </Container>
  );
};

export default Educations;
